import React, { useState, useEffect } from "react";
import { Ticket, Loader2, Lock, Minus, Plus, Clock, Shirt, Armchair, Camera, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { PageHero } from "@/components/Bits";
import { GALA, IMAGES } from "@/config/content";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import EmbeddedPayment from "@/components/EmbeddedPayment";

const POLICY_ICONS = [Shirt, Clock, Armchair, Camera];

export default function Gala() {
  const [qty, setQty] = useState(1);
  const [coverFee, setCoverFee] = useState(true);
  const [donor, setDonor] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [phase, setPhase] = useState("pay"); // pay | success
  const [clientSecret, setClientSecret] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    if (payOpen) window.__lenis?.stop();
    else window.__lenis?.start();
    return () => window.__lenis?.start();
  }, [payOpen]);

  const upd = (k) => (e) => setDonor((d) => ({ ...d, [k]: e.target.value }));
  const FEE_RATE = 0.029, FEE_FIXED = 0.30;
  const subtotal = GALA.price * qty;
  const fee = coverFee ? Math.round((subtotal * FEE_RATE + FEE_FIXED) * 100) / 100 : 0;
  const total = subtotal + fee;

  const buy = async (e) => {
    e.preventDefault();
    if (!donor.name.trim() || !donor.email.trim()) {
      toast.error("Please enter your name and email for your tickets.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "ticket", quantity: qty, cover_fee: coverFee,
        origin_url: window.location.origin, donor,
      });
      setClientSecret(data.client_secret);
      setSessionId(data.session_id);
      setPhase("pay");
      setPayOpen(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    setPhase("success");
    if (sessionId) api.get(`/payments/status/${sessionId}`).catch(() => {});
  };

  const inputCls = "w-full rounded-sm border border-gold/30 bg-ink px-4 py-4 text-lg text-ivory placeholder:text-ivory2/40 focus:border-gold";

  return (
    <div>
      <Seo title="The Annual Gala" description="Join EHSIMFFA for its annual fundraising Gala in support of scholarships for Ethiopian university students." path="/gala" />
      <PageHero kicker={GALA.subtitle} title={GALA.name} subtitle={GALA.intro} image={IMAGES.emperorColor} />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28 grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
        {/* Details */}
        <Reveal>
          <figure className="relative mb-10 mx-auto max-w-sm">
            <div className="absolute -inset-3 border border-gold/25" aria-hidden="true" />
            <img src={GALA.flyer} alt="2026 Annual Gala flyer — Emperor Haile Selassie I Memorial Foundation's Friends in America" className="relative w-full object-cover" loading="lazy" data-testid="gala-flyer" />
          </figure>
          <div className="grid gap-4 sm:grid-cols-3 mb-10">
            <div className="border border-gold/25 bg-ink2 p-6">
              <div className="kicker mb-2">Date</div>
              <div className="font-serif text-xl text-ivory">{GALA.date}</div>
            </div>
            <div className="border border-gold/25 bg-ink2 p-6">
              <div className="kicker mb-2">Time</div>
              <div className="font-serif text-xl text-ivory">{GALA.time}</div>
            </div>
            <div className="border border-gold/25 bg-ink2 p-6">
              <div className="kicker mb-2">Venue</div>
              <div className="font-serif text-xl text-ivory">{GALA.venue}</div>
              <div className="mt-1 text-sm text-ivory2/70">{GALA.address}</div>
            </div>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-ivory">Event details & policies</h2>
          <div className="mt-8 space-y-6">
            {GALA.policies.map((p, i) => {
              const Icon = POLICY_ICONS[i] || Ticket;
              return (
                <div key={p.label} className="flex gap-4 border-t border-gold/15 pt-6">
                  <Icon className="mt-1 h-6 w-6 shrink-0 text-gold" />
                  <div>
                    <h3 className="font-serif text-xl text-ivory">{p.label}</h3>
                    <p className="mt-1 text-ivory2/85 leading-relaxed">{p.value}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </Reveal>

        {/* Purchase card */}
        <Reveal delay={0.1}>
          <form onSubmit={buy} className="sticky top-28 border border-gold/30 bg-ink2 p-6 sm:p-8 rounded-sm" data-testid="gala-purchase">
            <div className="kicker flex items-center gap-2"><Ticket className="h-4 w-4" /> Purchase Tickets</div>
            <div className="mt-3 font-serif text-4xl text-ivory">${GALA.price} <span className="text-lg text-ivory2/60">per person</span></div>

            <div className="mt-6">
              <label className="mb-2 block text-sm font-semibold uppercase tracking-wider text-ivory2">Number of tickets</label>
              <div className="flex items-center gap-4">
                <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="gala-qty-minus"
                  className="flex h-12 w-12 items-center justify-center rounded-sm border border-gold/40 text-gold hover:bg-gold/10" aria-label="Decrease quantity">
                  <Minus className="h-5 w-5" />
                </button>
                <span className="w-12 text-center font-serif text-3xl text-ivory" data-testid="gala-qty">{qty}</span>
                <button type="button" onClick={() => setQty((q) => Math.min(10, q + 1))} data-testid="gala-qty-plus"
                  className="flex h-12 w-12 items-center justify-center rounded-sm border border-gold/40 text-gold hover:bg-gold/10" aria-label="Increase quantity">
                  <Plus className="h-5 w-5" />
                </button>
              </div>
            </div>

            <label className="mt-6 flex items-start gap-3 cursor-pointer rounded-sm border border-gold/25 bg-ink p-4">
              <input type="checkbox" checked={coverFee} onChange={(e) => setCoverFee(e.target.checked)} data-testid="gala-cover-fee"
                className="mt-1 h-5 w-5 accent-gold" />
              <span className="text-ivory2">
                <span className="text-ivory font-medium">Cover the processing fee</span> (+${fee.toFixed(2)})
                <span className="block text-sm text-ivory2/60">So 100% of your ticket price supports the scholarship fund.</span>
              </span>
            </label>

            <div className="mt-6 space-y-4">
              <input type="text" required placeholder="Full name" value={donor.name} onChange={upd("name")} className={inputCls} data-testid="gala-name" aria-label="Full name" />
              <input type="email" required placeholder="Email (for your tickets)" value={donor.email} onChange={upd("email")} className={inputCls} data-testid="gala-email" aria-label="Email" />
              <input type="tel" placeholder="Phone (optional)" value={donor.phone} onChange={upd("phone")} className={inputCls} data-testid="gala-phone" aria-label="Phone" />
            </div>

            <div className="mt-6 flex items-center justify-between border-t border-gold/20 pt-4 text-lg">
              <span className="text-ivory2">Total</span>
              <span className="font-serif text-3xl gold-text" data-testid="gala-total">${total.toFixed(2)}</span>
            </div>

            <button type="submit" disabled={loading} data-testid="gala-buy-btn"
              className="mt-6 flex w-full items-center justify-center gap-3 rounded-sm bg-gold py-5 text-lg font-bold uppercase tracking-wider text-ink transition-colors hover:bg-goldhi disabled:opacity-70">
              {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Preparing checkout…</> : <><Lock className="h-5 w-5" /> Buy Tickets</>}
            </button>
            <p className="mt-3 text-center text-sm text-ivory2/60">Secure payment via Stripe.</p>
          </form>
        </Reveal>
      </section>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent
          data-lenis-prevent
          className="w-[calc(100vw-1.5rem)] max-w-[460px] max-h-[92vh] overflow-y-auto overscroll-contain border border-gold/30 bg-ink2 text-ivory p-0 rounded-sm gap-0"
          data-testid="gala-pay-dialog"
        >
          {phase === "success" ? (
            <div className="px-6 py-10 text-center" data-testid="gala-success">
              <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
              <DialogTitle className="mt-5 font-serif text-3xl font-light text-ivory">You're going!</DialogTitle>
              <DialogDescription className="mt-3 text-ivory2 text-[15px] leading-relaxed">
                Your {qty} ticket(s) to the {GALA.name} are confirmed. A confirmation and receipt are on the way to {donor.email}. Please present it at the door.
              </DialogDescription>
              <div className="mt-6 border-t border-gold/20 pt-4 text-left text-sm text-ivory2/80">
                <p>{GALA.date} · {GALA.time}</p>
                <p>{GALA.venue}, {GALA.address}</p>
              </div>
              <button onClick={() => setPayOpen(false)} data-testid="gala-close-btn"
                className="mt-8 w-full rounded-sm bg-gold py-3.5 text-base font-bold uppercase tracking-wider text-ink hover:bg-goldhi">Done</button>
            </div>
          ) : (
            <div>
              <div className="sticky top-0 z-10 border-b border-gold/20 bg-ink2 px-5 py-4">
                <DialogTitle className="font-serif text-xl font-light text-ivory">Complete Your Purchase</DialogTitle>
                <DialogDescription className="text-[13px] text-ivory2/70">{qty} ticket(s) · ${total.toFixed(2)} total</DialogDescription>
              </div>
              <EmbeddedPayment clientSecret={clientSecret} onComplete={handleComplete} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
